import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const ApiDebug = () => {
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState({});

  const fetchDebugInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try to fetch from the correct API URL instead of localhost
      const response = await axios.get(`${API_URL}/debug`);
      setDebugInfo(response.data);
    } catch (err) {
      console.error('Error fetching API debug info:', err);
      setError('Failed to fetch API information');
      
      // Collect status info for each endpoint to help debug
      checkEndpointStatus();
    } finally {
      setLoading(false);
    }
  };

  const checkEndpointStatus = async () => {
    const endpoints = ['/meals', '/mealplans', '/shopping-list', '/ingredients'];
    const results = {};
    
    for (const endpoint of endpoints) {
      try {
        const start = Date.now();
        const response = await axios.get(`${API_URL}${endpoint}`, { 
          timeout: 5000,
          validateStatus: () => true // Accept any status code
        });
        const duration = Date.now() - start;
        
        results[endpoint] = {
          status: response.status,
          statusText: response.statusText,
          duration: `${duration}ms`,
          cors: !!(response.headers && response.headers['access-control-allow-origin']),
          error: null
        };
      } catch (err) {
        results[endpoint] = {
          status: 'Error',
          statusText: err.message,
          duration: 'N/A',
          cors: false,
          error: err.message
        };
      }
    }
    
    setApiStatus(results);
  };

  useEffect(() => {
    fetchDebugInfo();
  }, []);

  return (
    <div className="api-debug mt-4">
      <div className="card">
        <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">API Debug Information</h5>
          <button 
            className="btn btn-sm btn-light" 
            onClick={fetchDebugInfo}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="card-body">
          {loading && <p>Loading API information...</p>}
          
          {error && (
            <div className="alert alert-danger">
              <p>{error}</p>
              <p>Current API URL: <code>{API_URL}</code></p>
            </div>
          )}
          
          {Object.keys(apiStatus).length > 0 && (
            <div className="mt-3">
              <h6>Endpoint Status Check</h6>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Response Time</th>
                    <th>CORS Headers</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(apiStatus).map(([endpoint, info]) => (
                    <tr key={endpoint}>
                      <td><code>{endpoint}</code></td>
                      <td className={info.status === 200 ? 'text-success' : 'text-danger'}>
                        {info.status} {info.statusText}
                      </td>
                      <td>{info.duration}</td>
                      <td>{info.cors ? '✅' : '❌'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {debugInfo && !loading && (
            <div>
              <h6>API Status</h6>
              <p>
                Status: <span className={debugInfo.api_status === 200 ? 'text-success' : 'text-danger'}>
                  {debugInfo.api_status === 200 ? 'Online' : 'Error'} ({debugInfo.api_status})
                </span>
              </p>
              
              <h6>Quota Information</h6>
              <p>
                Used: <strong>{debugInfo.quota_info || 'Unknown'}</strong><br />
                Remaining: <strong>{debugInfo.quota_left || 'Unknown'}</strong>
              </p>
              
              <h6>Database Stats</h6>
              <ul>
                <li>Meals in database: {debugInfo.db_stats?.meals || 0}</li>
                <li>Ingredients in database: {debugInfo.db_stats?.ingredients || 0}</li>
                <li>Cached searches: {debugInfo.db_stats?.cached_searches || 0}</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiDebug; 